/**
 * Resolve a runtime path against the app's base URL.
 *
 * The client is served at '/app/' (see quasar.config.ts > build.publicPath), so
 * a root-absolute '/icons/…' resolves against the DOMAIN ROOT — which belongs
 * to the Astro marketing site — and 404s, even though the file sits in this
 * app's own public/. Every URL built at runtime must therefore go through here.
 *
 * This lives in its own module rather than in `services/constants` because ~26
 * test files `vi.mock('services/constants', …)`; a helper exported from there
 * would be undefined in every one of those suites. Keeping it unmocked means
 * those factories need no changes now or when the next helper is added.
 *
 * Only for URLs built at runtime. Static template bindings and Vite-processed
 * imports are already base-aware and must be left alone.
 *
 * See docs/developer/marketing-site.md → "Runtime asset URLs must go through
 * withBase".
 */
export const withBase = (path: string): string =>
  `${import.meta.env.BASE_URL.replace(/\/$/, '')}${path}`

/**
 * Resolve a *configured* asset path (a twin property such as a tile's
 * `headerIcon`) against the app base URL, tolerating values that are already
 * prefixed and ones that must be left alone.
 *
 * Dashboard configs store root-absolute paths like `/icons/nexus/heat.svg`,
 * which 404 against the domain root (the marketing site owns `/`). This makes
 * them base-aware without a data migration, and is idempotent so a config that
 * already says `/app/icons/...` does not become `/app/app/icons/...`.
 *
 * Absolute URLs, protocol-relative URLs, data URIs, inline SVG and relative
 * paths are returned unchanged — only a bare root-absolute path is rewritten.
 */
export const withConfiguredBase = (path: string | undefined): string | undefined => {
  if (!path) return undefined
  if (!path.startsWith('/') || path.startsWith('//')) return path
  const base = import.meta.env.BASE_URL.replace(/\/$/, '')
  if (!base || path.startsWith(`${base}/`) || path === base) return path
  return `${base}${path}`
}

const IMAGE_EXTENSION_RE = /\.(svg|png|jpe?g|gif|webp|avif|ico|bmp)$/i

/**
 * Tell an *image reference* apart from an *icon-set name* in a configured icon value.
 *
 * A tile's `headerIcon` (and the `icon` comment on a model) accepts both forms: dashboards
 * store image paths like `/icons/nexus/heat.svg`, while model comments and KQL payloads store
 * icon-set names like `ti-stats-up` or the legacy `mdi-alert`. The name form must reach
 * `q-icon` — which resolves it through the boot `iconMapFn` (see src/boot/icons.ts) — because
 * handing it to an `<img src>` renders nothing at all, with no error to show for it.
 *
 * A path carries a `/` or an image file extension, a scheme, or is protocol-relative; an
 * icon-set name is a bare kebab-case token. `data:` URIs and inline SVG are references too.
 */
export const isConfiguredIconImage = (value: string | undefined): boolean => {
  if (!value) return false
  if (/^(https?:|data:|\/\/)/i.test(value)) return true
  return value.includes('/') || IMAGE_EXTENSION_RE.test(value)
}
